/* The button, and the place where the product's restraint is most visible.
 *
 * A 2px radius rather than a pill, a 1px border rather than a shadow, a flat
 * fill rather than a gradient, and a 100ms colour change rather than a lift.
 * DESIGN_LANGUAGE.md §1 ("visual weight is spent on content, not on chrome")
 * and §5 (28px control height, compact) between them leave very little room for
 * decoration, which is the intent: in a dense interface the button that shouts
 * is the one that makes the table beside it harder to read.
 */

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  height: 40px;
  padding: 0 var(--space-5);
  border: var(--border-width-thin) solid transparent;
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  user-select: none;
  cursor: pointer;
  transition:
    background-color var(--duration-fast) var(--easing-standard),
    border-color var(--duration-fast) var(--easing-standard),
    box-shadow var(--duration-fast) var(--easing-standard),
    color var(--duration-fast) var(--easing-standard),
    transform var(--duration-fast) var(--easing-standard);
}

.button:active:not(:disabled):not([data-loading]) {
  transform: translateY(1px);
}

.button:disabled {
  opacity: var(--opacity-disabled);
  cursor: not-allowed;
}

.button[data-loading] {
  cursor: progress;
}

.sm {
  height: 32px;
  padding: 0 var(--space-3);
  font-size: var(--text-sm);
}

.label {
  /* `:empty` rather than a prop: an icon-only button passes its glyph as the
   * child, so there is nothing to collapse there, and a button whose label is
   * conditionally empty should not leave a gap behind. */
  display: contents;
}

.icon {
  display: inline-flex;
  flex: none;
  /* The optical centre of a 16px glyph sits slightly above the text baseline
   * box; a half-pixel back down makes icon and label look aligned rather than
   * measure aligned. */
  transform: translateY(-0.5px);
}

/* --- primary ---------------------------------------------------------- */

.primary {
  background: var(--color-accent-emphasis);
  color: var(--color-text-inverse);
  box-shadow:
    var(--shadow-control-rest),
    var(--shadow-inset-highlight);
}

.primary:hover:not(:disabled):not([data-loading]) {
  background: var(--color-accent-emphasis-hover);
  box-shadow: var(--shadow-control-raised);
}

.primary:active:not(:disabled) {
  background: var(--color-accent-emphasis-hover);
}

/* --- secondary: the default ------------------------------------------- */

.secondary {
  background: var(--color-surface);
  color: var(--color-text-primary);
  border-color: var(--color-border-default);
  box-shadow: var(--shadow-control-rest);
}

.secondary:hover:not(:disabled):not([data-loading]) {
  background: var(--color-surface-hover);
  border-color: var(--color-border-strong);
}

/* --- ghost ------------------------------------------------------------ */

.ghost {
  background: transparent;
  color: var(--color-text-secondary);
}

.ghost:hover:not(:disabled):not([data-loading]) {
  background: var(--color-surface-hover);
  color: var(--color-text-primary);
}

/* --- danger ----------------------------------------------------------- */

.danger {
  background: var(--color-danger-emphasis);
  color: var(--color-text-inverse);
  box-shadow: var(--shadow-low);
}

.danger:hover:not(:disabled):not([data-loading]) {
  background: var(--color-danger-emphasis-hover);
  box-shadow: var(--shadow-low), inset 0 0 0 1px var(--color-danger-hover-ring);
}

/* --- icon-only -------------------------------------------------------- */

.iconButton {
  width: var(--control-height);
  padding: 0;
}

.iconButton.sm {
  width: var(--control-height-sm);
}
